import React, { useMemo } from 'react';

import CompleteCellContentModal from '../CompleteCellContentModal.jsx'
import MultiSelectCellEditor from '../MultiSelectCellEditor.jsx';

import { DATA_TYPE_PROP_MAP } from '../../common/AgGrid/index.jsx';

const INVALID_CELL_STYLE = {color: "#a94442", backgroundColor: "#f2dede", borderColor: "#c9302c"}
const NON_EDITABLE_STYLE = {cursor: 'not-allowed', backgroundColor: "#e2e3e5", opacity: 0.7}
const UNDEFINED_VALUE = '#UNDEF_VALUE';

const parseSingleValue = (cellRendererParams, newValue) => {
  const copiedValue = cellRendererParams.optionMap.has(newValue);
  if (copiedValue) return newValue;

  const copiedLabel = getOptionByLabel(cellRendererParams.original.selection_options, newValue);
  if (copiedLabel) return copiedLabel.value;
}

// Check both value & label. If none, return value (copy pasting invalid values)
const selectionValueParser = ({newValue, colDef: { cellRendererParams }}) => {
  const singleValue = parseSingleValue(cellRendererParams, newValue);
  if (singleValue) return singleValue;

  let multipleOptions = newValue;
  try {
    multipleOptions = JSON.parse(newValue) 
  } catch (e) {
    console.warn(e)
  }

  if (Array.isArray(multipleOptions)) {
    return multipleOptions.map(opt => parseSingleValue(cellRendererParams, opt))
  }

  return newValue;
}

// Render array as text, format to option labels
const selectionValueFormatter = ({value, colDef: { cellRendererParams }}) => {
  if (value === UNDEFINED_VALUE) return '(Blanks)';
  if (Array.isArray(value)) {
    return value.map(val => cellRendererParams.optionMap.get(val) || 'undefined').join(', ')
  }

  return cellRendererParams.optionMap.get(value);
}

const getOptionValues = (options) => options.map(o => o.value);


// options: Array<{value: value, label: label}>
const getOptionByLabel = (options, label) => {
  return options?.find(o => o.label == label);
}

const validateSelectionCell = ({value, colDef: { cellRendererParams }}) => {
  if (!value) return true // No value, consider valid

  let checkAgainst = Array.isArray(value) ? value : [value];

  return checkAgainst.every(v => cellRendererParams.optionMap.has(v))
}

export const resolveSelectionProps = (column, props) => {
  props.filter = 'agSetColumnFilter';
  props.enablePivot = true;
  
  props.filterParams ||= {}
  const selectionOptions = [
    ...(column.exclude_blank_selection ? [] : [{value: UNDEFINED_VALUE}]),
    ...column.selection_options
  ];
  props.filterParams.values = getOptionValues(selectionOptions);
  props.filterParams.valueFormatter ||= selectionValueFormatter;
  props.filterParams.filterOptions = ['equals', 'notEqual', 'blank', 'notBlank'];
  
  props.valueFormatter ||= selectionValueFormatter;
  props.valueParser = selectionValueParser;

  props.cellEditorParams ||= {}
  props.cellEditorParams.values = getOptionValues(column.selection_options);
  props.cellEditorParams.options = column.selection_options;
  props.cellRendererParams.validate ||= validateSelectionCell;

  // Enable search
  props.cellEditorParams.allowTyping = true;
  props.cellEditorParams.filterList = true;
  props.cellEditorParams.highlightMatch = true;
  props.cellEditorParams.searchType = 'matchAny';  

  // Store as Map for quicker fetching
  props.cellRendererParams ||= {}
  props.cellRendererParams.optionMap = new Map(column.selection_options.map(c => [c.value, c.label]));
  if (column.use_option_set_hierarchy) {
    props.cellRendererParams.hierarchyOptionsIndex = (column.selection_options || []).reduce((map, option) => {
      const levelKey = `level|${option.level}`;
      const optionKey = `option|${option.level}|${option.value}`;
      const childKey = `children|${option.level}|${option.parent_option_set_key}|${option.parent_value}`;

      if (!map.has(levelKey)) map.set(levelKey, []);
      if (!map.has(childKey)) map.set(childKey, []);

      map.get(levelKey).push(option);
      map.get(childKey).push(option);
      map.set(optionKey, option);

      return map;
    }, new Map());
  }

  // if (column.use_option_set_hierarchy) {
  //   props.cellEditor = HierarchySelectCellEditor;  
  //   props.cellClass = 'overflow-visible';
  // } else 
  if (column.is_array) { // Multi select
    props.cellEditor = MultiSelectCellEditor;  
    //props.cellClass = 'overflow-visible';
  } else {
    props.cellEditor = 'agRichSelectCellEditor';
  }

  if (column.is_array) {
    props.cellRendererParams.processForClipboard = (params) => {
      return JSON.stringify(params.value.map(val => params.column.colDef.cellRendererParams.optionMap.get(val)));
    }
  } else { // Single select
    props.cellRendererParams.processForClipboard = (params) => {
      return params.column.colDef.cellRendererParams.optionMap.get(params.value);
    }
  }

  props.cellEditorPopup = true
  
  return props
}

// Dynamic props specific to datagrid
const resolveDynamicProps = (column, restrictions, canModifyNonEditableRows, editMode, colorColumnByHeaderGroup) => {
  let props = {...DATA_TYPE_PROP_MAP[column.column_type]};
  let editable = props.editable;

  props.editable = (params) => {
    // Disable editing when the column is marked as non-editable(System columns)
    if (editable === false || params.colDef.field === 'row_id') return false;

    const isEditable = (editable ?? ((params) => (params.data.editable !== false || !!canModifyNonEditableRows)));
    return isEditable(params) && isEditableBasedOnRestrictions(params.data, params.colDef.field, restrictions);
  }

  if (column.formula != null) props.editable = false; // Formula can never be edited by hand
  if (!editMode) props.editable = false; // Only when editMode 
  
  // Always add the column definition stored
  // Was a shallow copy, need to copy nested
  props.cellRendererParams = {...props.cellRendererParams}
  props.cellRendererParams.original = column

  if (column.is_selection){
    props = resolveSelectionProps(column, props)
  }
  
  if (column.column_type == 'long_text') {
    props.cellRenderer = LongTextModal
  }

  if (typeof props.cellStyle !== 'function') {
    props.cellStyle = (params) => {
      if (typeof params.colDef.cellRendererParams?.validate == 'function'
        && !params.colDef.cellRendererParams.validate(params)
      ) {
        return INVALID_CELL_STYLE;
      }

      if (editMode || params.editMode) {
        const isNotEditable = !(typeof params.colDef.editable === 'function'
          ? params.colDef.editable(params)
          : params.colDef.editable);

        if (isNotEditable) {
          return NON_EDITABLE_STYLE;
        }
      }

      return {};
    }
  }

  if (colorColumnByHeaderGroup) {
    const currentClass = props.cellClass;
    props.cellClass = (params) => {
      return `${currentClass || ''} ${params.value < 0 ? 'dg-negative-value' : ''} ${getHeaderGroupColorClass(params)}`;
    }
  }

  return {...props, ...(column.columnProps || {})}
}

// Check if a column can be edited based on the restrictions
const isEditableBasedOnRestrictions = (data, column, restrictions) => {
  if (!restrictions || !restrictions.type) return true;

  switch (restrictions.type) {
    case 'edit_columns':
      return restrictions.editable_columns?.includes(column);

    case 'locked_rows':
      return !restrictions.locked_rows?.includes(data['id']);

    case 'single_row_edit':
      return restrictions.enabled == true;

    case 'condition_based': {
      const condition = restrictions.condition?.editable_if || {};
      const matches = Object.entries(condition).every(
        ([key, val]) => data[key] == val
      );
      if (!matches) return false;
      return restrictions.condition.editable_columns?.includes(column);
    }

    default:
      return true;
  }
}

const getHeaderGroupColorClass = params => {
  let value = parseInt(params.column.getParent()?.groupId, 10);
  value = isNaN(value) ? -1 : value;
  return ` ag-cell-group-color-${(value + 1) % 4}`;
}

const LongTextModal = props => {
  return <CompleteCellContentModal cellContent={props.value} cellIdentifier={`${props.data?.id}-${props.original.id}`} title={props.colDef.headerName}/>
}

export const buildColumnDefs = (columns, options = {}) => {
  let grouped = _.groupBy(columns, 'group');
  let buildColDef = col => {
    return {
      chartDataType: col.chartDataType,
      field: col.accessor,
      headerName: col.name,
      headerComponentParams: {
        unitName: col.unit_name,
        description: col.description
      },
      resizable: true,
      hide: col.hasOwnProperty('visible') ? !col.visible : false,
      sortable: true,
      pinned: col.pinned,
      aggFunc: col.aggFunc,
      ...(options?.colorColumnByHeaderGroup && {
        headerClass: (params) => {
          return getHeaderGroupColorClass(params);
        }
      }),
      ...(resolveDynamicProps(col, options?.restrictions, options?.canModifyNonEditableRows, options?.editMode, options?.colorColumnByHeaderGroup))
    }
  };

  let colDefs = []

  columns.forEach(column => {
    // Add as grouped.
    if (column.group && column.group != '') {
      if (!grouped[column.group]) return; // Already processed, deleted from grouped
      colDefs.push({
        headerName: column.group,
        headerClass: (params) => {
          // Vary color between 4 different definitions
          return `ag-header-group-color-${(parseInt(params.columnGroup.groupId,10) + 1) % 4}`
        },    
        marryChildren: true,
        children: grouped[column.group].map(c => buildColDef(c))
      });
      // Delete to skip next columns
      delete grouped[column.group];
      return;
    }
    // Else add as root level
    colDefs.push(buildColDef(column));
  });
  return colDefs;
}

export default function useDynamicCols(columns, options) {
  return buildColumnDefs(columns, options);
}


export const useDynamicDefaultCols = (context, editMode, supressSorting) => {
  return useMemo( ()=> ({
    sortable: !supressSorting,
    editable: context?.permissions?.edit && editMode
  }),[editMode, supressSorting]);
}